import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { ZodFormUtilities } from '@jfw/angular/ecommerce/zod-forms';
import {
  FEEDBACK_DESCRIPTION_MAX_LENGTH,
  FeedbackClientContext,
  FeedbackDescriptionFieldSchema,
  FeedbackSubmitRequestSchema,
  FeedbackTypeEnum,
  FeedbackTypeFieldSchema,
} from '@jfw/types';
import { startWith } from 'rxjs';
import { FEEDBACK_DIALOG_HOST } from './feedback-dialog-host';

type DialogState = 'form' | 'sending' | 'sent';

/**
 * EXT-6172: the header's "Send feedback" dialog. Built to the Claude Design
 * mockup (`Wholesale Feedback.dc.html`). Opened fresh on every click, so the
 * form resets after a send without any explicit reset logic.
 *
 * EXT-6239: shared by Platform Portal and Dealer Portal. Everything
 * portal-specific (who's signed in, the context, where it's sent, the "My
 * feedback" page) comes from the portal's FEEDBACK_DIALOG_HOST.
 */
@Component({
  selector: 'app-feedback-dialog',
  templateUrl: './feedback-dialog.component.html',
  styleUrl: './feedback-dialog.component.scss',
  imports: [
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    TranslocoDirective,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FeedbackDialogComponent {
  private readonly dialogRef = inject(MatDialogRef<FeedbackDialogComponent>);
  private readonly host = inject(FEEDBACK_DIALOG_HOST);
  private readonly transloco = inject(TranslocoService);

  // One per dialog open: a retry after a failure reuses it, so the server
  // stores at most one record however many times Send is pressed.
  private readonly clientSubmissionId = crypto.randomUUID();

  protected readonly context: FeedbackClientContext = this.host.snapshot();
  protected readonly user = this.host.user;
  protected readonly teamName = this.host.teamName;
  protected readonly hasMyFeedback = !!this.host.openMyFeedback;

  // EXT-5701: labels come from `shared.feedbackDialog.types.*` /
  // `.userTypes.*`, kept equal in English to common-types'
  // FEEDBACK_TYPE_LABELS / FEEDBACK_USER_TYPE_LABELS (asserted in the spec).
  protected readonly typeOptions = Object.values(FeedbackTypeEnum);
  protected readonly userType = this.host.userType;
  protected readonly maxLength = FEEDBACK_DESCRIPTION_MAX_LENGTH;

  // Single-field schemas, so each control validates against exactly the
  // constraint the API enforces for that field.
  protected readonly form = new FormGroup({
    type: new FormControl<FeedbackTypeEnum | null>(null, [
      ZodFormUtilities.zodFormControlValidator(FeedbackTypeFieldSchema),
    ]),
    description: new FormControl<string>('', {
      nonNullable: true,
      validators: [
        ZodFormUtilities.zodFormControlValidator(
          FeedbackDescriptionFieldSchema,
        ),
      ],
    }),
  });

  protected readonly state = signal<DialogState>('form');
  protected readonly errorMessage = signal<string | null>(null);

  private readonly formStatus = toSignal(
    this.form.statusChanges.pipe(startWith(this.form.status)),
  );
  protected readonly descriptionLength = toSignal(
    this.form.controls.description.valueChanges.pipe(startWith('')),
    { initialValue: '' },
  );
  protected readonly canSend = computed(
    () => this.formStatus() === 'VALID' && this.state() === 'form',
  );

  protected descriptionError(): string | undefined {
    const control = this.form.controls.description;
    return control.touched || control.dirty
      ? ZodFormUtilities.getFirstSchemaFieldErrorMessage(control)
      : undefined;
  }

  protected close(): void {
    if (this.state() !== 'sending') {
      this.dialogRef.close();
    }
  }

  /** EXT-6244: see what's happened to feedback already sent. */
  protected openMyFeedback(): void {
    if (this.state() === 'sending' || !this.host.openMyFeedback) {
      return;
    }
    this.dialogRef.close();
    this.host.openMyFeedback();
  }

  protected send(): void {
    if (!this.canSend()) {
      return;
    }
    // Final gate against the full request schema the API parses.
    const parsed = FeedbackSubmitRequestSchema.safeParse({
      clientSubmissionId: this.clientSubmissionId,
      type: this.form.controls.type.value,
      description: this.form.controls.description.value,
      context: this.context,
    });
    if (!parsed.success) {
      this.errorMessage.set(
        parsed.error.issues[0]?.message ??
          this.transloco.translate('shared.feedbackDialog.validationFailed'),
      );
      return;
    }

    this.state.set('sending');
    this.errorMessage.set(null);
    this.dialogRef.disableClose = true;
    this.host.submit(parsed.data).subscribe({
      next: () => {
        this.dialogRef.disableClose = false;
        this.state.set('sent');
      },
      error: (error: unknown) => {
        this.dialogRef.disableClose = false;
        this.state.set('form');
        this.errorMessage.set(
          error instanceof HttpErrorResponse &&
            error.status === 429 &&
            typeof error.error === 'string'
            ? error.error
            : this.transloco.translate('shared.feedbackDialog.sendFailed'),
        );
      },
    });
  }
}
